import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTransferDetailsForm } from "../../hooks/sendMoney/useTransferDetailsForm";
import useExistingRecipientContinue from "../../hooks/sendMoney/useExistingRecipientContinue";
import { parseIconToken } from "../../services/utilityFunctions";
import i18n from "../../i18n/config";
import { ROUTES } from "../../constants/clientRoutes";
import { SEND_MONEY_TRANSFER_METHOD_IDS } from "../../constants/enums";

// types
import { RootState } from "../../types/redux";

// ui components
import FlowScreen from "../../components/Layouts/FlowScreen";
import SingleColumnLayout from "../../components/Layouts/SingleColumnLayout";
import Action from "../../components/Common/Action";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import DynamicField from "../../components/DynamicForms/DynamicField";
import ActionLink from "../../components/Common/ActionLink";
import SendMoneyLimitsModal from "../../components/SendMoney/TransferDetails/SendMoneyLimitsModal";
import SendMoney24hLimitModal from "../../components/SendMoney/TransferDetails/SendMoney24hLimitModal";
import SendMoneyWalletModal from "../../components/SendMoney/TransferDetails/SendMoneyWalletModal";
import WalletRow from "../../components/SendMoney/TransferDetails/WalletRow";
import RecipientGetsRow from "../../components/SendMoney/TransferDetails/RecipientGetsRow";
import SendMoneyQuoteDisplay from "../../components/SendMoney/TransferDetails/SendMoneyQuoteDisplay";
import CurrencySuffix from "../../components/SendMoney/TransferDetails/CurrencySuffix";
import SendMoneyFooter from "../../components/SendMoney/SendMoneyFooter";

const SendMoneyTransferDetails = (): React.ReactElement => {
  const history = useHistory();
  const cardCurrency = useSelector((state: RootState) => state.card.currencyCode);

  const transferMethod = useSelector(
    (state: RootState) => state.sendMoney.transferMethod
  );

  const selectedOperator = useSelector(
    (state: RootState) => state.sendMoney.selectedOperator
  );

  const selectedRecipient = useSelector(
    (state: RootState) => state.sendMoney.selectedRecipient
  );

  const isMobileWallet =
    transferMethod === SEND_MONEY_TRANSFER_METHOD_IDS.MOBILE_WALLET;

  const [limitsOpen, setLimitsOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);

  const { form, fields, loading, quote, walletOperatorDisplay, showRolling24hLink } = useTransferDetailsForm();
  const { control } = form;

  const { validating, continueExistingRecipient } = useExistingRecipientContinue();

  const [rolling24hOpen, setRolling24hOpen] = useState(false);

  const goBack = () => history.goBack();

  const isExistingRecipient = selectedRecipient !== null;
  const isNewRecipient = selectedRecipient === null;
  const hasQuote = quote !== null;

  // saved recipient: the operator is fixed on the account server-side — validate needs only the quote
  const hasFixedOperator = isExistingRecipient;

  // new recipient: operator is picked through the wallet modal
  const hasSelectedOperator = isNewRecipient && selectedOperator !== null;

  const hasOperator = hasFixedOperator || hasSelectedOperator;

  // the fixed operator's display is informational; no display, no row
  const hasOperatorDisplay = walletOperatorDisplay !== null;

  const showFixedWalletRow = isMobileWallet && hasQuote && isExistingRecipient && hasOperatorDisplay;
  const showSelectableWalletRow = isMobileWallet && hasQuote && isNewRecipient;

  const walletRequirementMet = hasOperator || !isMobileWallet;
  const canContinue = hasQuote && walletRequirementMet && !validating;

  const onContinue = () => {
    if (!quote || !canContinue) return;

    if (isExistingRecipient) {
      continueExistingRecipient(quote);
      return;
    }

    history.push(ROUTES.SEND_MONEY.RECIPIENT_TYPE);
  };

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.SEND_MONEY.TRANSFER_DETAILS.TITLE")}
      showBackButton
      customBackButtonFunc={goBack}
      className="v3"
    >
      <SingleColumnLayout>
        {fields.map((field) => (
          <DynamicField
            key={field.name}
            field={field}
            control={control}
            suffix={cardCurrency ? <CurrencySuffix currency={cardCurrency} /> : undefined}
            inputClassName="bold text-primary font-size-xxl"
            belowControl={
              showRolling24hLink ? (
                <ActionLink
                  classes="text-blue-700 no-underline bold font-size-sm mt-8"
                  clickFunc={() => setRolling24hOpen(true)}
                >
                  {i18n.t("PAGES.SEND_MONEY.TRANSFER_DETAILS.HOW_24H_LIMIT_WORKS")}
                </ActionLink>
              ) : undefined
            }
          />
        ))}

        {/* Recipient gets */}
        <RecipientGetsRow
          amount={quote ? quote.receiveAmount : null}
          onLimits={() => setLimitsOpen(true)}
        />

        {/* Wallet operator */}
        {showFixedWalletRow && (
          <WalletRow operatorDisplay={parseIconToken(walletOperatorDisplay)} />
        )}

        {showSelectableWalletRow && (
          <WalletRow onEdit={() => setWalletOpen(true)} />
        )}

        <SendMoneyQuoteDisplay loading={loading} quote={quote} />

        {hasQuote &&
          <Action
            onClick={onContinue}
            loading={validating}
            disabled={!canContinue}
            className="w-full mt-0"
            type="button"
            title={i18n.t("COMMON.CONTINUE")}
          />
        }
        <SendMoneyFooter variant="small" disclaimers={quote?.disclaimers} />
      </SingleColumnLayout>

      <ApiErrorModal />
      <SendMoneyLimitsModal open={limitsOpen} setClose={setLimitsOpen} />
      {(isMobileWallet && !isExistingRecipient) && (
        <SendMoneyWalletModal open={walletOpen} setClose={setWalletOpen} quoteId={quote?.quoteId} />
      )}
      <SendMoney24hLimitModal open={rolling24hOpen} setClose={setRolling24hOpen} />
    </FlowScreen>
  );
};

export default SendMoneyTransferDetails;